import { Field, Formik, FormikErrors } from 'formik';
import { FormikUtils } from '../../shared/utils';
import { CompanyContact, Guid } from '../../shared/models';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import React from 'react';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../shared/components';
import { Autocomplete, Grid, TextField } from '@mui/material';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys, useCompanyContactsQuery, useDealQuery } from '../../shared/hooks';
import {
	ESignDocument,
	ESignDocumentReminderFrequency,
	ESignDocumentReminderFrequencyMap,
} from '../../shared/models/ESignDocument';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { getApiResponseData } from '../../shared/utils/axios';

interface SendContractDocumentDrawerProps {
	dealGuid: string | undefined;
	eSignDoc: ESignDocument | null | undefined;
	productSubscriptionGuid: Guid | null;
	companySubscriptionGuid: Guid | null;
	companyGuid?: Guid;
	isProductSubscription: boolean;
	onClose: () => void;
	open: boolean;
}

export const SendContractDocumentDrawer: React.FC<SendContractDocumentDrawerProps> = ({
	dealGuid,
	eSignDoc,
	productSubscriptionGuid,
	companySubscriptionGuid,
	companyGuid,
	isProductSubscription,
	open,
	onClose,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);
	const dealQuery = useDealQuery(dealGuid);
	const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	const contactsQuery = useCompanyContactsQuery(companyGuid, {
		enabled: !!companyGuid,
		retry: false,
	});

	const subscriptionGuid = isProductSubscription ? productSubscriptionGuid : companySubscriptionGuid;

	var validateForm = (values: SendContractDocumentDrawerValues): SendContractDocumentDrawerErrors => {
		const errors: SendContractDocumentDrawerErrors = {};

		if (!values.toEmail || values.toEmail.length === 0) {
			errors.toEmail = 'Must enter at least one email address.';
		}

		return errors;
	};

	const uploadSubscriptionAgreementMutation = useMutation(
		async ({ values }: SendContractDocumentDrawerMutationVariables) => {
			const endpoint = isProductSubscription
				? CerebroApiEndpoints.Deals.SendContractForSigning(dealGuid, subscriptionGuid!)
				: CerebroApiEndpoints.Company.SendContractForSigning(companyGuid, subscriptionGuid!);

			const { data, error } = await getApiResponseData<void>(api.post(endpoint, values));

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.Root(dealGuid));
				snackbar.enqueueSnackbar('Contract successfully sent for digital signature.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading>Send Contract for E-Signature</CerebroHeading>

			<Formik
				validate={validateForm}
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(uploadSubscriptionAgreementMutation.mutate)}
				initialValues={{
					toEmail:
						contactsQuery.data && contactsQuery.data.length > 0
							? [getToEmail(contactsQuery.data, dealQuery.data?.dataRoomGuid)!].filter(Boolean) 
							: [],
					comment: '',
					notifyOthers: true,
					message: '',
					reminderFrequency: 'None',
				}}
			>
				{({ isSubmitting, handleSubmit, setFieldValue }) => {
					const handleEmailsChange = (_: any, newValue: string[]) => {
						const filtered = newValue.filter(e => EMAIL_REGEX.test(e));
						setFieldValue('toEmail', filtered);
					};

					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								{!!eSignDoc && (
									<Grid item xs={12}>
										<CerebroText sx={{ marginBottom: 3 }}>
											<FontAwesomeIcon icon={faInfoCircle} size="sm" /> &nbsp; Sending contract to a
											new recipient will cancel any agreements that have already been sent for
											signing.
										</CerebroText>
									</Grid>
								)}

								<Grid item xs={12}>
									<CerebroLabel htmlFor="toEmail" >
										To
									</CerebroLabel>
									<CerebroText variant="hint" sx={{ marginBottom: 2 }}>
										Type each email and press Enter to add it before typing the next one.
									</CerebroText>
									<Field name="toEmail">
										{({ form }: { field: any; form: any }) => {
											const { values, touched, errors } = form;
											return (
												<Autocomplete<string, true, false, true>
													multiple
													freeSolo
													options={contactsQuery.data?.map(c => c.emailAddress) ?? []}
													value={values.toEmail}
													onChange={handleEmailsChange}
													onKeyDown={e => { if ((e.key === 'Enter' || e.key === 'Tab') && e.currentTarget === e.target) { e.preventDefault(); } }}
													renderInput={params => (
														<TextField
															{...params}
															id="toEmail"
															label="Emails"
															error={Boolean(touched.toEmail && errors.toEmail)}
															helperText={touched.toEmail && errors.toEmail}
														/>
													)}
												/>
											);
										}}
									</Field>
								</Grid>
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel htmlFor="reminderFrequency" sx={{ marginTop: 6 }}>
									Reminder Frequency
								</CerebroLabel>

								<Grid container spacing={3}>
									<Grid item xs={6}>
										<Field
											id="reminderFrequency"
											as={CerebroDropdown}
											name="reminderFrequency"
											disabled={isSubmitting}
										>
											{Object.entries(ESignDocumentReminderFrequencyMap).map(
												([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												)
											)}
										</Field>
									</Grid>
								</Grid>
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel htmlFor="message" sx={{ marginTop: 6 }}>
									Message
								</CerebroLabel>
								<CerebroText variant="hint">
									An optional message to the participants, describing what is being sent or why their
									signature is required.
								</CerebroText>

								<Field rows={4} as={CerebroTextarea} disabled={isSubmitting} id="message" name="message" />
							</Grid>

							{!!eSignDoc && (
								<>
									<Grid item xs={12}>
										<CerebroLabel htmlFor="comment" sx={{ marginTop: 6 }}>
											Cancellation Message
										</CerebroLabel>
										<CerebroText variant="hint">
											An optional comment describing to the recipients why you want to cancel the
											transaction.
										</CerebroText>

										<Field
											rows={4}
											as={CerebroTextarea}
											disabled={isSubmitting}
											id="comment"
											name="comment"
										/>
									</Grid>

									<Grid item xs={12}>
										<CerebroLabel htmlFor="notifyOthers" sx={{ marginTop: 6 }}>
											Notify Others
										</CerebroLabel>
										<CerebroText variant="hint">
											Whether or not you would like the recipients to be notified that the transaction
											has been cancelled. The default value is true.
										</CerebroText>

										<Field
											id="notifyOthers"
											as={CerebroYesNo}
											disabled={isSubmitting}
											name="notifyOthers"
										/>
									</Grid>
								</>
							)}

							<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
								Send
							</CerebroPrimaryButton>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

function getToEmail(contacts: CompanyContact[], dataRoomGuid: Guid): string | null {
	if (!dataRoomGuid) return null;

	if (!contacts || contacts.length === 0) return null;

	const activeContact = contacts.find(c => c.isActive && !!c.emailAddress);
	
	return activeContact?.emailAddress ?? null;
}

type SendContractDocumentDrawerErrors = FormikErrors<SendContractDocumentDrawerValues>;
type SendContractDocumentDrawerMutationVariables =
	FormikUtils.MutateOnSubmitVariables<SendContractDocumentDrawerValues>;
interface SendContractDocumentDrawerValues {
	toEmail: string[];
	comment: string;
	notifyOthers: boolean;
	message: string;
	reminderFrequency: ESignDocumentReminderFrequency;
}
